import { useTranslation } from 'react-i18next';

import { TerritoryCircleFlag } from '~/components/TerritoryCircleFlag/TerritoryCircleFlag';
import { cn } from '~/lib/cn';
import type { TerritoryCode } from '~/lib/territories/territories';
import { RESTRICTED_FLAG_TERRITORIES } from '~/lib/territories/territories';

export interface TerritoryLabelProps {
  code: TerritoryCode;
  className?: string;
  flagClassName?: string;
  textClassName?: string;
}

/**
 * Component used to display a territory's flag and name. Takes care of hiding
 * the flag for territories that shouldn't display it.
 * @param code - The territory code.
 * @param className - Additional class names to apply to the label.
 * @param flagClassName - Additional class names to apply to the flag.
 * @param textClassName - Additional class names to apply to the text (e.g., 'truncate' for text overflow).
 * @returns A div containing the flag and name of the territory.
 */
function TerritoryLabel({
  code,
  className,
  flagClassName,
  textClassName,
}: TerritoryLabelProps) {
  const { t } = useTranslation('territories');

  // Only show the flag for territories that are not restricted.
  const showFlag = !RESTRICTED_FLAG_TERRITORIES[code];

  return (
    <div className={cn('flex items-center gap-sm', className)}>
      {showFlag ? (
        <TerritoryCircleFlag code={code} className={flagClassName} />
      ) : null}
      <span className={textClassName}>{t(`names.${code}`)}</span>
    </div>
  );
}

TerritoryLabel.displayName = 'TerritoryLabel';

export { TerritoryLabel };
